import { I18n } from 'aws-amplify/utils';
import { useEffect, useRef, useState } from 'react';
import { ApiNamePaths } from '@/constants/rest-api-name-path';
import type { Reservation } from '@/features/driver/types/driveDetail';
import { ReservationStatusCode } from '@/features/driver/types/driveDetail';
import type { DropoffInformationType } from '@/features/map/types/dropoffInformationType';
import type { PickupDropoffInformationType } from '@/features/map/types/pickupDropoffInformationType';
import type { PickupInformationType } from '@/features/map/types/pickupInformationType';
import type { VehicleOperationScheduleType } from '@/features/vehicleOperationSchedule/types/vehicleOperationSchedule';
import { apiGet } from '@/modules/hooks/use-query';

const INITIAL_VEHICLE_OPERATION_SCHEDULE: VehicleOperationScheduleType = {
  vehicleOperationScheduleId: 0,
  operationScheduleId: 0,
  operationScheduleName: '',
  operationZoneId: 0,
  operationZoneName: '',
  direction: '',
  date: '',
  driveReservationCount: 0,
  drivePassengerCount: 0,
  drivePrice: 0,
  reservationDeadlineDateTime: '',
  actualOperationEndDateTime: '',
  reservationList: [
    {
      reservationId: 0,
      userId: 0,
      userName: '',
      gender: '',
      address: '',
      pickupWaypointId: 0,
      pickupWaypointNumber: '',
      pickupWaypointName: '',
      pickupLatitude: '',
      pickupLongitude: '',
      pickupWaypointPosition: { lat: 0, lng: 0 },
      estimatedPickupDatetime: '',
      latestEstimatedPickupDatetime: '',
      dropoffWaypointId: 0,
      dropoffWaypointNumber: '',
      dropoffWaypointName: '',
      dropoffLatitude: '',
      dropoffLongitude: '',
      dropoffWaypointPosition: { lat: 0, lng: 0 },
      estimatedDropoffDatetime: '',
      latestEstimatedDropoffDatetime: '',
      price: 0,
      paymentMethod: 0,
      message: '',
      passengerTypeList: [{ passengerTypeId: 0, passengerTypeName: '', passengerCount: 0 }],
      pickupWaypointSequence: '',
      dropoffWaypointSequence: '',
    },
  ],
};

type UseDriveDataParams = {
  vehicleOperationScheduleId: string | null;
  storybookMode: boolean;
  mockVehicleOperationSchedule?: VehicleOperationScheduleType;
  mockIsStartButtonPressed?: boolean;
  mockShowRouteChangeModal?: boolean;
  onError: (message: string) => void;
};

type UseDriveDataReturn = {
  vehicleOperationSchedule: VehicleOperationScheduleType;
  reservationList: Reservation[];
  detailDisabled: boolean;
  isStartButtonPressed: boolean;
  setIsStartButtonPressed: React.Dispatch<React.SetStateAction<boolean>>;
  realTimeDetectionFlag: number;
  pickupInformation: PickupInformationType[];
  dropoffInformation: DropoffInformationType[];
  pickupDropoffInformation: PickupDropoffInformationType[];
  routeChangeModalShowFromMock: boolean;
  newReservationListFromMock: Reservation[];
};

const buildPickupInformation = (data: VehicleOperationScheduleType): PickupInformationType[] => {
  const convertedArray: PickupInformationType[] = [];
  data.reservationList.forEach((reservation) => {
    const {
      pickupWaypointId,
      pickupWaypointNumber,
      pickupWaypointName,
      latestEstimatedPickupDatetime,
      pickupWaypointSequence,
      reservationId,
      userName,
      pickupLatitude,
      pickupLongitude,
    } = reservation;

    const existing = convertedArray.find((item) => item.pickupWaypointId === pickupWaypointId);
    if (!existing) {
      convertedArray.push({
        pickupWaypointId,
        pickupWaypointNumber,
        pickupWaypointName,
        latestEstimatedPickupDatetime,
        pickupWaypointSequence,
        pickupWaypointPosition: { lat: +pickupLatitude, lng: +pickupLongitude },
        reservationIdList: [
          {
            reservationId,
            userName,
            latestEstimatedDatetime: latestEstimatedPickupDatetime,
            waypointSequence: pickupWaypointSequence,
          },
        ],
      });
    } else {
      existing.reservationIdList.push({
        reservationId,
        userName,
        latestEstimatedDatetime: latestEstimatedPickupDatetime,
        waypointSequence: pickupWaypointSequence,
      });
    }
  });

  return convertedArray;
};

const buildDropoffInformation = (data: VehicleOperationScheduleType): DropoffInformationType[] => {
  const convertedArray: DropoffInformationType[] = [];
  data.reservationList.forEach((reservation) => {
    const {
      dropoffWaypointId,
      dropoffWaypointNumber,
      dropoffWaypointName,
      latestEstimatedDropoffDatetime,
      dropoffWaypointSequence,
      reservationId,
      userName,
      dropoffLatitude,
      dropoffLongitude,
    } = reservation;

    const existing = convertedArray.find((item) => item.dropoffWaypointId === dropoffWaypointId);
    if (!existing) {
      convertedArray.push({
        dropoffWaypointId,
        dropoffWaypointNumber,
        dropoffWaypointName,
        latestEstimatedDropoffDatetime,
        dropoffWaypointSequence,
        dropoffWaypointPosition: { lat: +dropoffLatitude, lng: +dropoffLongitude },
        reservationIdList: [
          {
            reservationId,
            userName,
            latestEstimatedDatetime: latestEstimatedDropoffDatetime,
            waypointSequence: dropoffWaypointSequence,
          },
        ],
      });
    } else {
      existing.reservationIdList.push({
        reservationId,
        userName,
        latestEstimatedDatetime: latestEstimatedDropoffDatetime,
        waypointSequence: dropoffWaypointSequence,
      });
    }
  });

  return convertedArray;
};

export const useDriveData = ({
  vehicleOperationScheduleId,
  storybookMode,
  mockVehicleOperationSchedule,
  mockIsStartButtonPressed = false,
  mockShowRouteChangeModal = false,
  onError,
}: UseDriveDataParams): UseDriveDataReturn => {
  const isInitialMount = useRef(true);
  const isInitialMount2 = useRef(true);

  const [vehicleOperationSchedule, setVehicleOperationSchedule] = useState<VehicleOperationScheduleType>(
    INITIAL_VEHICLE_OPERATION_SCHEDULE,
  );
  const [reservationList, setReservationList] = useState<Reservation[]>([]);
  const [detailDisabled, setDetailDisabled] = useState(true);
  const [isStartButtonPressed, setIsStartButtonPressed] = useState<boolean>(mockIsStartButtonPressed);
  const [realTimeDetectionFlag, setRealTimeDetectionFlag] = useState<number>(0);
  const [pickupInformation, setPickupInformation] = useState<PickupInformationType[]>([]);
  const [dropoffInformation, setDropoffInformation] = useState<DropoffInformationType[]>([]);
  const [pickupDropoffInformation, setPickupDropoffInformation] = useState<PickupDropoffInformationType[]>([]);

  // Storybookモック専用の値（実機では使用されない）
  const [routeChangeModalShowFromMock, setRouteChangeModalShowFromMock] = useState(false);
  const [newReservationListFromMock, setNewReservationListFromMock] = useState<Reservation[]>([]);

  // Storybookモックデータの初期化
  useEffect(() => {
    if (!storybookMode || !mockVehicleOperationSchedule) return;

    setVehicleOperationSchedule(mockVehicleOperationSchedule);
    setReservationList(mockVehicleOperationSchedule.reservationList as unknown as Reservation[]);
    setDetailDisabled(false);
    setIsStartButtonPressed(mockIsStartButtonPressed);
    setPickupInformation(buildPickupInformation(mockVehicleOperationSchedule));
    setDropoffInformation(buildDropoffInformation(mockVehicleOperationSchedule));

    if (mockShowRouteChangeModal) {
      setRouteChangeModalShowFromMock(true);
      setNewReservationListFromMock(mockVehicleOperationSchedule.reservationList as unknown as Reservation[]);
    }
  }, [storybookMode, mockVehicleOperationSchedule, mockIsStartButtonPressed, mockShowRouteChangeModal]);

  // 初回マウント時にAPIからドライブデータを取得
  useEffect(() => {
    if (storybookMode) return;

    const fetchDriveData = async (id: string) => {
      try {
        const res = await apiGet(ApiNamePaths.drivesGetApi.apiName, `${ApiNamePaths.drivesGetApi.apiPath}/${id}`);
        const data = res as VehicleOperationScheduleType;
        if (data) {
          const reservations = data.reservationList as unknown as Reservation[];
          const allReservationCompleted = reservations.every(
            (reservation) =>
              (Number(reservation.status) as unknown as ReservationStatusCode) ===
              ReservationStatusCode.RESERVATION_COMPLETED,
          );
          if (!allReservationCompleted) {
            setIsStartButtonPressed(true);
          }
          setReservationList(data.reservationList as unknown as Reservation[]);
          setDetailDisabled(false);
          setVehicleOperationSchedule(data);
          setPickupInformation(buildPickupInformation(data));
          setDropoffInformation(buildDropoffInformation(data));
          setRealTimeDetectionFlag(1);
        } else {
          setRealTimeDetectionFlag(2);
        }
      } catch (error) {
        const err = error as { message: string };
        onError(I18n.get(err.message) as string);
        setRealTimeDetectionFlag(2);
      }
    };

    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (vehicleOperationScheduleId !== null) {
        void fetchDriveData(vehicleOperationScheduleId);
      }
    }
  }, [vehicleOperationScheduleId, storybookMode, onError]);

  // 乗車と降車の地点が一致する場合の統合処理
  useEffect(() => {
    if (
      !isInitialMount2.current ||
      !pickupInformation ||
      !dropoffInformation ||
      pickupInformation.length === 0 ||
      dropoffInformation.length === 0
    ) {
      return;
    }

    isInitialMount2.current = false;

    const findMatchingDropoff = (pickupId: number) =>
      dropoffInformation.find((item) => item.dropoffWaypointId === pickupId);

    const newPickupDropoff: PickupDropoffInformationType[] = [];
    const updatedPickup = pickupInformation.filter((pickupItem) => {
      const matchingDropoff = findMatchingDropoff(pickupItem.pickupWaypointId);
      if (!matchingDropoff) return true;

      newPickupDropoff.push({
        pickupWaypointId: pickupItem.pickupWaypointId,
        latestEstimatedPickupDatetime: pickupItem.latestEstimatedPickupDatetime,
        pickupWaypointSequence: pickupItem.pickupWaypointSequence,
        pickupWaypointNumber: pickupItem.pickupWaypointNumber,
        pickupWaypointName: pickupItem.pickupWaypointName,
        pickupWaypointPosition: pickupItem.pickupWaypointPosition,
        dropoffWaypointId: matchingDropoff.dropoffWaypointId,
        latestEstimatedDropoffDatetime: matchingDropoff.latestEstimatedDropoffDatetime,
        dropoffWaypointSequence: matchingDropoff.dropoffWaypointSequence,
        dropoffWaypointNumber: matchingDropoff.dropoffWaypointNumber,
        dropoffWaypointName: matchingDropoff.dropoffWaypointName,
        dropoffWaypointPosition: matchingDropoff.dropoffWaypointPosition,
        pickupReservationIdList: pickupItem.reservationIdList,
        dropoffReservationIdList: matchingDropoff.reservationIdList,
      });

      return false;
    });

    const updatedDropoff = dropoffInformation.filter(
      (dropoffItem) =>
        !pickupInformation.some((pickupItem) => pickupItem.pickupWaypointId === dropoffItem.dropoffWaypointId),
    );

    setPickupDropoffInformation((prev) => [...prev, ...newPickupDropoff]);
    setPickupInformation(updatedPickup);
    setDropoffInformation(updatedDropoff);
  }, [pickupInformation, dropoffInformation]);

  return {
    vehicleOperationSchedule,
    reservationList,
    detailDisabled,
    isStartButtonPressed,
    setIsStartButtonPressed,
    realTimeDetectionFlag,
    pickupInformation,
    dropoffInformation,
    pickupDropoffInformation,
    routeChangeModalShowFromMock,
    newReservationListFromMock,
  };
};
